import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { useSortableList } from './hooks';
import { SortableItem } from './SortableItem';
import type { SortableItemBase, SortableVariant } from './types';

/**
 * アイテムをレンダリングする関数の型
 * @template T - SortableItemBaseを拡張するアイテム型
 */
type RenderItemFunction<T extends SortableItemBase> = React.ComponentProps<
  typeof SortableItem<T>
>['renderItem'];

/**
 * SortableListのprops
 * @template T - SortableItemBaseを拡張するアイテム型
 */
type Props<T extends SortableItemBase> = {
  /** ソート可能なアイテムの配列 */
  items: T[];
  /** アイテムの順序が変更されたときのコールバック */
  onReorder: (items: T[]) => void;
  /** 各アイテムをレンダリングする関数 */
  renderItem: RenderItemFunction<T>;
  /**
   * 描画バリアント。デフォルト: `'list'`
   * - `'list'`: 各アイテムを `<div>` でラップする（既存互換）
   * - `'table'`: 各アイテムを `<tr>` でラップする。必ず `<Table>` の `<tbody>` 内に配置すること
   */
  variant?: SortableVariant;
};

/**
 * ドラッグ&ドロップで並び替え可能なリストコンポーネント
 *
 * NOTE: React.FCはジェネリクスを直接サポートしないため、React.FC型を使用せずアロー関数の引数にPropsを指定して型定義しています
 *
 * @template T - SortableItemBaseを拡張するアイテム型
 * @param {Props<T>} props - コンポーネントのprops
 * @param {T[]} props.items - ソート可能なアイテムの配列
 * @param {function} props.onReorder - アイテムの順序が変更されたときのコールバック
 * @param {function} props.renderItem - 各アイテムをレンダリングする関数
 * @param {SortableVariant} [props.variant='list'] - 描画バリアント（'list' | 'table'）
 * @returns {JSX.Element} ドラッグ&ドロップ可能なリスト
 *
 * @example
 * <SortableList items={items} onReorder={onReorder} renderItem={renderItem} />
 */
export const SortableList = <T extends SortableItemBase>({
  items,
  onReorder,
  renderItem,
  variant,
}: Props<T>) => {
  const {
    state: { sensors, collisionDetection, sortingStrategy },
    actions: { handleDragEnd },
  } = useSortableList({ items, onReorder });

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(item => item.id)} strategy={sortingStrategy}>
        {items.map(item => (
          <SortableItem key={item.id} item={item} renderItem={renderItem} variant={variant} />
        ))}
      </SortableContext>
    </DndContext>
  );
};
